import { useCallback, useEffect, useState } from "react";
import type { ReportFinalization } from "../../../shared/wire/research.ts";
import { EXECUTION_MODE_LABEL } from "../../../shared/research/labels.ts";
import type { ComponentProps } from "./index";
import { Document } from "./Document";
import { openInApp, RESEARCH_CHANGED } from "./Research";

export type ReportBadgeState = ReportFinalization | { status: "loading" } | { status: "error"; message: string };

export function reportFinalizationUrl(sessionId: string, source: string): string {
	return `/api/sessions/${encodeURIComponent(sessionId)}/research/report?path=${encodeURIComponent(source)}`;
}

/** Whether the report a session shows was finalized, asked when shown and again after a research change in this browser. */
function useReportFinalization(sessionId: string, source: string): ReportBadgeState {
	const [state, setState] = useState<ReportBadgeState>({ status: "loading" });
	const load = useCallback(() => {
		fetch(reportFinalizationUrl(sessionId, source), { credentials: "include" })
			.then(async (res) => {
				const data = (await res.json().catch(() => null)) as (ReportFinalization & { error?: string }) | null;
				if (!res.ok || !data?.status) throw new Error(data?.error ?? `HTTP ${res.status}`);
				setState(data);
			})
			.catch((err: unknown) => setState({ status: "error", message: err instanceof Error ? err.message : String(err) }));
	}, [sessionId, source]);
	useEffect(() => {
		load();
		window.addEventListener(RESEARCH_CHANGED, load);
		return () => window.removeEventListener(RESEARCH_CHANGED, load);
	}, [load]);
	return state;
}

/**
 * Beside a saved report: finalized, with its finalized artifact; or not finalized, its verification unknown. A failed
 * lookup says so and never looks finalized. Nothing while loading, for a missing file, or outside research.
 */
export function ReportBadgeView({ state }: { state: ReportBadgeState }): React.ReactNode {
	switch (state.status) {
		case "finalized":
			return (
				<div className="report-badge finalized">
					<span className="report-badge-state">已定稿</span>
					<span className="hint">执行方式 {EXECUTION_MODE_LABEL[state.report.executionMode] ?? state.report.executionMode}</span>
					<a href={state.artifactHref} target="_blank" rel="noreferrer noopener">
						定稿版本
					</a>
					<a href={state.pageHref} onClick={(e) => openInApp(e, state.pageHref)}>
						研究记录
					</a>
				</div>
			);
		case "legacy":
			return (
				<div className="report-badge legacy">
					<span className="report-badge-state">未定稿</span>
					<span className="hint">核验情况 未知</span>
				</div>
			);
		case "error":
			return <p className="report-badge error">定稿状态读取失败：{state.message}</p>;
		default:
			return null;
	}
}

function ReportBadge({ sessionId, source }: { sessionId: string; source: string }) {
	return <ReportBadgeView state={useReportFinalization(sessionId, source)} />;
}

/** show_report: the report exactly as saved, with its finalization badge. */
export function ReportDocument(props: ComponentProps): React.ReactNode {
	const source = typeof props.args.source === "string" ? props.args.source : undefined;
	return <Document {...props} badge={source ? <ReportBadge sessionId={props.sessionId} source={source} /> : undefined} />;
}
